import { useEffect, useState } from "react";
import { X, LifeBuoy, Check } from "lucide-react";
import { toast } from "sonner";
import { requestRefund, myRefunds } from "@/lib/api";

const REASONS = [
  { value: "not_using", label: "Not using it enough" },
  { value: "too_expensive", label: "Too expensive" },
  { value: "missing_feature", label: "Missing a feature we need" },
  { value: "didnt_help", label: "Didn't help my child" },
  { value: "accidental", label: "Signed up by mistake" },
  { value: "other", label: "Other" },
];

export default function RefundRequestModal({ open, onClose }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setDetails("");
    myRefunds().then((rows) => setRecent(rows?.[0] || null)).catch(() => setRecent(null));
  }, [open]);

  if (!open) return null;

  const pending = recent?.status === "pending";

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) {
      toast.error("Please pick a reason");
      return;
    }
    setSubmitting(true);
    try {
      await requestRefund(reason, details.trim());
      toast.success("Refund request sent, we'll be in touch within one working day");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong, please try again";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="refund-modal"
      onClick={onClose}
    >
      <div
        className="nb-card bg-white dark:bg-zinc-900 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b nb-border border-l-0 border-r-0 border-t-0 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LifeBuoy className="w-5 h-5" strokeWidth={2.5} />
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Request a refund</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="refund-close"
            className="nb-border rounded-lg p-1.5 hover:nb-shadow"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {pending ? (
          <div className="p-6 space-y-4" data-testid="refund-pending">
            <div className="nb-border rounded-xl p-4 bg-[#FFE066] dark:bg-[#F6E05E] text-black">
              <div className="font-display font-extrabold mb-1">Already under review</div>
              <p className="text-sm font-medium">
                You submitted a request on {new Date(recent.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}. We'll reply by email within one working day.
              </p>
            </div>
            <button
              onClick={onClose}
              className="nb-button px-4 py-2 w-full"
              data-testid="refund-pending-close"
            >
              OK, got it
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-5">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              We review every request personally, usually within one working day. Pick a reason and tell us what happened.
            </p>

            <div>
              <div className="label-caps mb-2">Reason</div>
              <div className="space-y-2" data-testid="refund-reasons">
                {REASONS.map((r) => (
                  <label
                    key={r.value}
                    className={`nb-border rounded-xl p-3 flex items-center gap-3 cursor-pointer transition ${
                      reason === r.value
                        ? "bg-[#D4C4FB] dark:bg-[#B794F4] text-black"
                        : "bg-white dark:bg-zinc-900 hover:nb-shadow"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={(e) => setReason(e.target.value)}
                      className="sr-only"
                      data-testid={`refund-reason-${r.value}`}
                    />
                    <div
                      className={`w-5 h-5 nb-border rounded-full flex items-center justify-center flex-shrink-0 ${
                        reason === r.value ? "bg-black" : "bg-white dark:bg-zinc-900"
                      }`}
                    >
                      {reason === r.value && <Check className="w-3 h-3 text-white" strokeWidth={4} />}
                    </div>
                    <span className="font-bold text-sm">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="refund-details" className="label-caps mb-2 block">
                Anything else? <span className="normal-case text-zinc-400 ml-1">(optional)</span>
              </label>
              <textarea
                id="refund-details"
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, 1500))}
                data-testid="refund-details"
                rows={4}
                placeholder="Tell us what went wrong, the more we know, the better we can help."
                className="w-full nb-border rounded-xl p-3 text-sm font-medium bg-white dark:bg-zinc-900 focus:outline-none focus:nb-shadow"
              />
              <div className="text-xs text-zinc-400 font-bold mt-1 text-right">{details.length}/1500</div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="nb-border rounded-xl px-4 py-2.5 font-bold text-sm flex-1 hover:nb-shadow"
                data-testid="refund-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !reason}
                className="nb-button px-4 py-2.5 flex-1 bg-[#D4C4FB] dark:bg-[#B794F4] text-black disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="refund-submit"
              >
                {submitting ? "Sending…" : "Send request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
